import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { TelecallersApiService, TelecallerResponse } from './telecallers/telecallers-api.service';
import { ProgramsApiService } from './programs/programs-api.service';
import { HospitalsApiService } from './hospitals/hospitals-api.service';
import { LabsApiService } from './labs/labs-api.service';
import { DoctorsApiService } from './doctors/doctors-api.service';

export interface TelecallerOption {
    id: string;
    name: string;
}

export interface ProgramOption {
    id: string;
    name: string;
}

export interface HospitalOption {
    id: string;
    name: string;
}

export interface LabOption {
    id: string;
    name: string;
}

export interface DoctorOption {
    id: string;
    name: string;
}

interface CacheEntry<T> {
    data: T[];
    timestamp: number;
}

/**
 * Centralized service for caching frequently accessed lookup data
 * Reduces duplicate API calls for telecallers, programs, and other reference data
 */
@Injectable({
    providedIn: 'root'
})
export class LookupDataService {
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    // BehaviorSubjects for reactive caching
    private telecallersCache$ = new BehaviorSubject<TelecallerOption[]>([]);
    private programsCache$ = new BehaviorSubject<ProgramOption[]>([]);
    private hospitalsCache$ = new BehaviorSubject<HospitalOption[]>([]);
    private labsCache$ = new BehaviorSubject<LabOption[]>([]);
    private doctorsCache$ = new BehaviorSubject<DoctorOption[]>([]);

    // Cache metadata
    private telecallersLastFetch = 0;
    private programsLastFetch = 0;
    private hospitalsLastFetch = 0;
    private labsLastFetch = 0;
    private doctorsLastFetch = 0;

    // Loading states
    private telecallersLoading = false;
    private programsLoading = false;
    private hospitalsLoading = false;
    private labsLoading = false;
    private doctorsLoading = false;

    // Maps for quick ID lookups
    private telecallerMap = new Map<string, string>();
    private programMap = new Map<string, string>();
    private hospitalMap = new Map<string, string>();
    private labMap = new Map<string, string>();
    private doctorMap = new Map<string, string>();

    constructor(
        private telecallersApi: TelecallersApiService,
        private programsApi: ProgramsApiService,
        private hospitalsApi: HospitalsApiService,
        private labsApi: LabsApiService,
        private doctorsApi: DoctorsApiService
    ) { }

    /**
     * Get telecallers with caching
     * @param forceRefresh - Force refresh even if cache is valid
     * @returns Observable of telecaller options
     */
    getTelecallers(forceRefresh = false): Observable<TelecallerOption[]> {
        const now = Date.now();
        const cacheValid = (now - this.telecallersLastFetch) < this.CACHE_DURATION;

        // Return cached data if valid and not forcing refresh
        if (!forceRefresh && cacheValid && this.telecallersCache$.value.length > 0) {
            return this.telecallersCache$.asObservable();
        }

        // Prevent duplicate simultaneous requests
        if (this.telecallersLoading) {
            return this.telecallersCache$.asObservable();
        }

        // Fetch fresh data
        this.telecallersLoading = true;
        this.telecallersApi.getTelecallers(1, 100).pipe(
            tap((response: any) => {
                const users = response?.users || [];
                const telecallers = users.map((t: any) => {
                    const id = t.id || t._id || '';
                    const name = t.name ||
                        t.full_name ||
                        `${t.first_name || ''} ${t.last_name || ''}`.trim() ||
                        'Unknown';

                    // Update lookup map
                    this.telecallerMap.set(id, name);

                    return { id, name };
                });

                this.telecallersCache$.next(telecallers);
                this.telecallersLastFetch = now;
                this.telecallersLoading = false;
            }),
            catchError(error => {
                console.error('Error fetching telecallers:', error);
                this.telecallersLoading = false;
                return of([]);
            })
        ).subscribe();

        return this.telecallersCache$.asObservable();
    }

    /**
     * Get programs with caching
     * @param forceRefresh - Force refresh even if cache is valid
     * @returns Observable of program options
     */
    getPrograms(forceRefresh = false): Observable<ProgramOption[]> {
        const now = Date.now();
        const cacheValid = (now - this.programsLastFetch) < this.CACHE_DURATION;

        // Return cached data if valid and not forcing refresh
        if (!forceRefresh && cacheValid && this.programsCache$.value.length > 0) {
            return this.programsCache$.asObservable();
        }

        // Prevent duplicate simultaneous requests
        if (this.programsLoading) {
            return this.programsCache$.asObservable();
        }

        // Fetch fresh data
        this.programsLoading = true;
        this.programsApi.getPrograms(1, 100).pipe(
            tap((response: any) => {
                const programData = response?.data || response;
                const programsList = programData?.programs || programData || [];
                const programs = programsList.map((p: any) => {
                    const id = p._id || p.id || '';
                    const name = p.program_name || p.programName || p.name || 'Unknown';

                    // Update lookup map
                    this.programMap.set(id, name);

                    return { id, name };
                });

                this.programsCache$.next(programs);
                this.programsLastFetch = now;
                this.programsLoading = false;
            }),
            catchError(error => {
                console.error('Error fetching programs:', error);
                this.programsLoading = false;
                return of([]);
            })
        ).subscribe();

        return this.programsCache$.asObservable();
    }

    /**
     * Get telecaller name by ID from cache
     * @param id - Telecaller ID
     * @returns Telecaller name or 'Unassigned'
     */
    getTelecallerName(id: string | undefined): string {
        if (!id) return 'Unassigned';
        return this.telecallerMap.get(id) || 'Unassigned';
    }

    /**
     * Get program name by ID from cache
     * @param id - Program ID
     * @returns Program name or 'Unknown'
     */
    getProgramName(id: string | undefined): string {
        if (!id) return 'Unknown';
        return this.programMap.get(id) || 'Unknown';
    }

    /**
     * Get multiple program names by IDs from cache
     * @param ids - Array of program IDs
     * @returns Comma-separated program names or 'N/A'
     */
    getProgramNames(ids: string[] | undefined): string {
        if (!ids || ids.length === 0) return 'N/A';
        const names = ids
            .map(id => this.programMap.get(id))
            .filter(name => name)
            .join(', ');
        return names || 'N/A';
    }

    /**
     * Get hospitals with caching
     * @param forceRefresh - Force refresh even if cache is valid
     * @returns Observable of hospital options
     */
    getHospitals(forceRefresh = false): Observable<HospitalOption[]> {
        const now = Date.now();
        const cacheValid = (now - this.hospitalsLastFetch) < this.CACHE_DURATION;

        if (!forceRefresh && cacheValid && this.hospitalsCache$.value.length > 0) {
            return this.hospitalsCache$.asObservable();
        }

        if (this.hospitalsLoading) {
            return this.hospitalsCache$.asObservable();
        }

        this.hospitalsLoading = true;
        this.hospitalsApi.getHospitals(1, 1000).pipe(
            tap((response: any) => {
                const data = response?.data || response;
                const hospitalsList = data?.hospitals || data || [];
                const hospitals = hospitalsList.map((h: any) => {
                    const id = h.id || h._id || '';
                    const name = h.name || h.hospitalName || 'Unknown';

                    this.hospitalMap.set(id, name);

                    return { id, name };
                });

                this.hospitalsCache$.next(hospitals);
                this.hospitalsLastFetch = now;
                this.hospitalsLoading = false;
            }),
            catchError(error => {
                console.error('Error fetching hospitals:', error);
                this.hospitalsLoading = false;
                return of([]);
            })
        ).subscribe();

        return this.hospitalsCache$.asObservable();
    }

    /**
     * Get labs with caching
     * @param forceRefresh - Force refresh even if cache is valid
     * @returns Observable of lab options
     */
    getLabs(forceRefresh = false): Observable<LabOption[]> {
        const now = Date.now();
        const cacheValid = (now - this.labsLastFetch) < this.CACHE_DURATION;

        if (!forceRefresh && cacheValid && this.labsCache$.value.length > 0) {
            return this.labsCache$.asObservable();
        }

        if (this.labsLoading) {
            return this.labsCache$.asObservable();
        }

        this.labsLoading = true;
        this.labsApi.getLabs(1, 1000).pipe(
            tap((response: any) => {
                const data = response?.data || response;
                const labsList = data?.labs || data || [];
                const labs = labsList.map((l: any) => {
                    const id = l.id || l._id || '';
                    const name = l.name || l.labName || 'Unknown';

                    this.labMap.set(id, name);

                    return { id, name };
                });

                this.labsCache$.next(labs);
                this.labsLastFetch = now;
                this.labsLoading = false;
            }),
            catchError(error => {
                console.error('Error fetching labs:', error);
                this.labsLoading = false;
                return of([]);
            })
        ).subscribe();

        return this.labsCache$.asObservable();
    }

    /**
     * Get doctors with caching
     * @param forceRefresh - Force refresh even if cache is valid
     * @returns Observable of doctor options
     */
    getDoctors(forceRefresh = false): Observable<DoctorOption[]> {
        const now = Date.now();
        const cacheValid = (now - this.doctorsLastFetch) < this.CACHE_DURATION;

        if (!forceRefresh && cacheValid && this.doctorsCache$.value.length > 0) {
            return this.doctorsCache$.asObservable();
        }

        if (this.doctorsLoading) {
            return this.doctorsCache$.asObservable();
        }

        this.doctorsLoading = true;
        this.doctorsApi.getDoctors(1, 1000).pipe(
            tap((response: any) => {
                const data = response?.data || response;
                const doctorsList = data?.doctors || data || [];
                const doctors = doctorsList.map((d: any) => {
                    const id = d.id || d._id || '';
                    const name = d.name || d.doctorName || 'Unknown';

                    this.doctorMap.set(id, name);

                    return { id, name };
                });

                this.doctorsCache$.next(doctors);
                this.doctorsLastFetch = now;
                this.doctorsLoading = false;
            }),
            catchError(error => {
                console.error('Error fetching doctors:', error);
                this.doctorsLoading = false;
                return of([]);
            })
        ).subscribe();

        return this.doctorsCache$.asObservable();
    }

    /**
     * Get hospital name by ID from cache
     * @param id - Hospital ID
     * @returns Hospital name or 'N/A'
     */
    getHospitalName(id: string | undefined | null): string {
        if (!id) return 'N/A';
        return this.hospitalMap.get(id) || 'N/A';
    }

    /**
     * Get lab name by ID from cache
     * @param id - Lab ID
     * @returns Lab name or 'N/A'
     */
    getLabName(id: string | undefined | null): string {
        if (!id) return 'N/A';
        return this.labMap.get(id) || 'N/A';
    }

    /**
     * Get doctor name by ID from cache
     * @param id - Doctor ID
     * @returns Doctor name or 'N/A'
     */
    getDoctorName(id: string | undefined | null): string {
        if (!id) return 'N/A';
        return this.doctorMap.get(id) || 'N/A';
    }

    /**
     * Clear all caches
     */
    clearCache(): void {
        this.telecallersCache$.next([]);
        this.programsCache$.next([]);
        this.hospitalsCache$.next([]);
        this.labsCache$.next([]);
        this.doctorsCache$.next([]);
        this.telecallerMap.clear();
        this.programMap.clear();
        this.hospitalMap.clear();
        this.labMap.clear();
        this.doctorMap.clear();
        this.telecallersLastFetch = 0;
        this.programsLastFetch = 0;
        this.hospitalsLastFetch = 0;
        this.labsLastFetch = 0;
        this.doctorsLastFetch = 0;
    }

    /**
     * Preload all lookup data
     * Useful for app initialization
     */
    preloadAll(): void {
        this.getTelecallers(true).subscribe();
        this.getPrograms(true).subscribe();
        this.getHospitals(true).subscribe();
        this.getLabs(true).subscribe();
        this.getDoctors(true).subscribe();
    }
}
